import React, { useEffect, useState } from 'react';
import { reportToSegment, types } from '@smartcar/morse';
import { arrayOf, bool, func, number, objectOf, shape, string } from 'prop-types';
import { Box, Tab, Tabs, Typography } from '@mui/material';

import staticText from '../../../../../../../../localization/Application/webhooks';
import { TabsNav } from '../../../../../../../../global-styles/components';
import { Banner, SignalSearch, SignalSelection, TabPanel } from '../../../../../../../../components';

import { LabelCounter } from '../../../../../../../../components/SignalSelection/styles';

import { getSelectedSignalCount } from '../../util';

const WebhookSignals = ({
  signals,
  selectedSignals,
  handleSignalClick,
  setNextEnabled,
  totalSignalRecords,
  errorFetchingSignals,
}) => {
  const signalsText = staticText.addModal.stepperSections[1];
  const [currentTab, setCurrentTab] = useState(0);
  const [searchActive, setSearchActive] = useState(false);

  const [showWarning, setShowWarning] = useState(false);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  useEffect(() => {
    const selectedSignalCount = getSelectedSignalCount(signals, selectedSignals);
    setShowWarning(selectedSignalCount === totalSignalRecords);
    setNextEnabled(selectedSignals.size > 0);
  }, [selectedSignals.size, totalSignalRecords]);

  useEffect(() => {
    reportToSegment(types.PAGE, 'Webhook Form - Signals');
  }, []);

  return (
    <Box display="flex" flexDirection="column" width="100%">
      <Typography variant="h1">{signalsText.title}</Typography>
      <Typography variant="body2" color="text.secondary" mt={2} mb={3}>{signalsText.description}</Typography>

      {errorFetchingSignals && (
        <Box mb={3}>
          <Banner
            severity="warning"
            hideIcon={false}
          >
            <Typography variant="body2">
              {staticText.addModal.errors.fetchingSignals}
            </Typography>
          </Banner>
        </Box>
      )}

      {Object.keys(signals).length > 0 && (
        <React.Fragment>
          <SignalSearch
            signals={signals}
            selectedSignals={selectedSignals}
            setSearchActive={setSearchActive}
            handleSignalClick={handleSignalClick}
          />

          {selectedSignals.size > 0 && showWarning && (
            <Banner severity="warning" styles={{ mb: 3 }}>
              <Typography variant="bold" sx={theme => ({ mb: 1, fontSize: theme.typography.body2.fontSize })}>
                {signalsText.warning.title}
              </Typography>
              <Typography variant="body2">
                {signalsText.warning.description}
              </Typography>
            </Banner>
          )}

          {!searchActive && (
            <React.Fragment>
              <TabsNav color="transparent" component="nav" elevation={0}>
                <Tabs
                  value={currentTab}
                  onChange={handleTabChange}
                  indicatorColor="primary"
                >
                  {signalsText.tabs.map(({ label }, i) => (
                    <Tab
                      key={label}
                      label={i === 0 ? label : (
                        <span>
                          {label}
                          <LabelCounter>
                            {getSelectedSignalCount(signals, selectedSignals)}
                          </LabelCounter>
                        </span>
                      )}
                      value={i}
                      active="primary"
                      disabled={i === 1 && selectedSignals.size === 0}
                      sx={{ ...(i === 1 && selectedSignals.size === 0 && { opacity: 0.5 }) }}
                    />
                  ))}
                </Tabs>
              </TabsNav>

              <Box flexGrow={1} overflow="hidden" pb={3}>
                {signalsText.tabs.map(({ label }, i) => (
                  <TabPanel
                    key={label}
                    index={i}
                    value={currentTab}
                    pageName={`${label}-signals`}
                    boxProps={{ height: '100%' }}
                    style={{ height: '100%' }}
                  >
                    <SignalSelection
                      signals={signals}
                      selectedSignals={selectedSignals}
                      handleSignalClick={handleSignalClick}
                      showSelectedSignalsOnly={i === 1}
                    />
                  </TabPanel>
                ))}
              </Box>
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </Box>
  );
};

export default WebhookSignals;

WebhookSignals.propTypes = {
  signals: objectOf({
    [string]: arrayOf(shape({
      name: string,
      groupName: string,
      description: string,
    })),
  }).isRequired,
  selectedSignals: objectOf(string).isRequired,
  handleSignalClick: func.isRequired,
  setNextEnabled: func.isRequired,
  totalSignalRecords: number.isRequired,
  errorFetchingSignals: bool.isRequired,
};
